'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

const FooterSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState<number>(2025);

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date()?.getFullYear());
  }, []);

  if (!isHydrated) {
    return (
      <footer className="bg-primary text-primary-foreground py-12">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="h-48 bg-primary/80 rounded-lg animate-pulse" />
        </div>
      </footer>);

  }

  return (
    <footer className="bg-primary text-primary-foreground py-12 lg:py-16">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-accent rounded-lg">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">

                  <path
                    d="M12 2L2 7L12 12L22 7L12 2Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-accent-foreground" />

                  <path
                    d="M2 17L12 22L22 17"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-accent-foreground" />

                  <path
                    d="M2 12L12 17L22 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-accent-foreground" />

                </svg>
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-headline font-bold leading-tight">
                  Nasza Hipoteka
                </span>
                <span className="text-xs opacity-80 leading-tight">
                  Ekspert Finansowy
                </span>
              </div>
            </div>
            <p className="text-sm opacity-80 mb-4">
              Pomagamy osobom prywatnym w uzyskaniu najlepszych warunków kredytu hipotecznego od 2010 roku. Działamy na terenie całego kraju. Nasi doradcy współpracują z Expander Advisors Sp. z o.o.
            </p>
            <div className="flex items-center gap-3">
              <Icon name="ShieldCheckIcon" size={20} variant="solid" className="text-success" />
              <span className="text-xs opacity-80">Licencjonowany doradca finansowy</span>
            </div>
          </div>

          <div>
            <h3 className="text-base font-headline font-bold mb-4">Usługi</h3>
            <ul className="space-y-2">
              <li>
                <a
                  href="#personal-financing"
                  onClick={(e) => {
                    e?.preventDefault();
                    const section = document.getElementById('personal-financing');
                    if (section) {
                      const offsetPosition = section?.offsetTop - 80;
                      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                    }
                  }}
                  className="text-sm opacity-80 transition-opacity duration-250 hover:opacity-100">

                  Kredyty hipoteczne
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-base font-headline font-bold mb-4">Informacje</h3>
            <ul className="space-y-2">
              <li>
                <a
                  href="#about"
                  onClick={(e) => {
                    e?.preventDefault();
                    const section = document.getElementById('about');
                    if (section) {
                      const offsetPosition = section?.offsetTop - 80;
                      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                    }
                  }}
                  className="text-sm opacity-80 transition-opacity duration-250 hover:opacity-100">

                  O nas
                </a>
              </li>
              <li>
                <a
                  href="#calculator"
                  onClick={(e) => {
                    e?.preventDefault();
                    const section = document.getElementById('calculator');
                    if (section) {
                      const offsetPosition = section?.offsetTop - 80;
                      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                    }
                  }}
                  className="text-sm opacity-80 transition-opacity duration-250 hover:opacity-100">

                  Kalkulator kredytowy
                </a>
              </li>
              <li>
                <a href="#" className="text-sm opacity-80 transition-opacity duration-250 hover:opacity-100">
                  Blog
                </a>
              </li>
              <li>
                <a href="#" className="text-sm opacity-80 transition-opacity duration-250 hover:opacity-100">
                  Polityka prywatności
                </a>
              </li>
              <li>
                <a href="#" className="text-sm opacity-80 transition-opacity duration-250 hover:opacity-100">
                  Regulamin
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-base font-headline font-bold mb-4">Kontakt</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <Icon name="PhoneIcon" size={16} variant="outline" className="mt-0.5 flex-shrink-0" />
                <a
                  href="tel:+48667009004"
                  className="text-sm opacity-80 transition-opacity duration-250 hover:opacity-100">

                  667 009 004
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="EnvelopeIcon" size={16} variant="outline" className="mt-0.5 flex-shrink-0" />
                <a
                  href="mailto:biuro@naszahipoteka.pl"
                  className="text-sm opacity-80 transition-opacity duration-250 hover:opacity-100">

                  biuro@naszahipoteka.pl
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="MapPinIcon" size={16} variant="outline" className="mt-0.5 flex-shrink-0" />
                <span className="text-sm opacity-80">Mikołów, Starokościelna 12
                </span>
              </li>
              <li className="flex items-start gap-2">
                <a
                  href="https://www.facebook.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  className="opacity-80 hover:opacity-100 transition-opacity duration-250 flex items-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  <span className="text-sm opacity-80">Facebook</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-primary-foreground/20 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm opacity-80 text-center md:text-left">
              © {currentYear} Nasza Hipoteka. Wszelkie prawa zastrzeżone.
            </p>
            <div className="flex items-center gap-4">
              <p className="text-sm opacity-80 text-center md:text-right">
                Stronę wykonano: <a href="mailto:milana.kalisz@gmail.com" className="underline hover:opacity-100 transition-opacity">Milana Kalisz</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>);

};

export default FooterSection;